/**
 * Customers API
 *
 * CRUD operations for customer/client entities.
 * Customers are linked to invoices and provide e-invoicing data (SDI, PEC).
 */

import {
  createEnvelope,
  createErrorEnvelope,
  type Envelope,
  type ISODate,
} from '@business-assistant/shared';

import { auditTrailService } from '../audit/auditTrail';

import type {
  Customer,
  CustomerType,
  Address,
  CustomerCreateData,
  CustomerUpdateData,
  CustomerFilters,
  CustomerWithStats,
} from '../types';

// ============================================================================
// IN-MEMORY STORAGE
// ============================================================================

const customersStore = new Map<string, Customer>();
let customerCounter = 1;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Generate a new customer ID
 */
function generateCustomerId(): string {
  return `cst-${String(customerCounter++).padStart(6, '0')}`;
}

/**
 * Get customer display name
 */
export function getCustomerDisplayName(customer: Customer): string {
  if (customer.type === 'company' && customer.companyName) {
    return customer.companyName;
  }
  return `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || customer.email;
}

/**
 * Get customer tax identifier
 */
export function getCustomerTaxId(customer: Customer): string | undefined {
  return customer.vatNumber || customer.fiscalCode;
}

/**
 * Validate Italian VAT number (Partita IVA)
 */
export function isValidItalianVAT(vatNumber: string): boolean {
  // Remove IT prefix if present
  const vat = vatNumber.replace(/^IT/i, '').trim();

  // Must be 11 digits
  if (!/^\d{11}$/.test(vat)) {
    return false;
  }

  // Luhn check for Italian VAT
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    const digit = parseInt(vat[i], 10);
    if (i % 2 === 0) {
      sum += digit;
    } else {
      const doubled = digit * 2;
      sum += doubled > 9 ? doubled - 9 : doubled;
    }
  }

  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === parseInt(vat[10], 10);
}

/**
 * Validate Italian Fiscal Code (Codice Fiscale)
 */
export function isValidItalianFiscalCode(fiscalCode: string): boolean {
  const code = fiscalCode.toUpperCase().trim();

  // Must be 16 characters
  if (!/^[A-Z0-9]{16}$/.test(code)) {
    return false;
  }

  // Basic format check: 6 letters + 2 digits + 1 letter + 2 digits + 1 letter + 3 digits + 1 letter
  const pattern = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/;
  return pattern.test(code);
}

/**
 * Validate SDI recipient code
 */
export function isValidSDICode(code: string): boolean {
  // 7 alphanumeric characters for B2B, "0000000" for B2C/private
  return /^[A-Z0-9]{7}$/i.test(code);
}

// ============================================================================
// CRUD OPERATIONS
// ============================================================================

/**
 * Create a new customer
 */
export async function createCustomer(
  data: CustomerCreateData
): Promise<Envelope<Customer>> {
  try {
    // Validate required fields based on type
    if (data.type === 'company' && !data.companyName) {
      return createErrorEnvelope('VALIDATION_ERROR', 'Company name is required for company customers');
    }

    if (data.type === 'individual' && (!data.firstName || !data.lastName)) {
      return createErrorEnvelope('VALIDATION_ERROR', 'First and last name are required for individual customers');
    }

    // Validate VAT number if provided
    if (data.vatNumber && !isValidItalianVAT(data.vatNumber)) {
      return createErrorEnvelope('VALIDATION_ERROR', 'Invalid Italian VAT number (Partita IVA)');
    }

    // Validate fiscal code if provided
    if (data.fiscalCode && !isValidItalianFiscalCode(data.fiscalCode)) {
      return createErrorEnvelope('VALIDATION_ERROR', 'Invalid Italian fiscal code (Codice Fiscale)');
    }

    // Validate SDI code if provided
    if (data.sdiRecipientCode && !isValidSDICode(data.sdiRecipientCode)) {
      return createErrorEnvelope('VALIDATION_ERROR', 'Invalid SDI recipient code (must be 7 alphanumeric characters)');
    }

    const id = generateCustomerId();
    const now = new Date().toISOString();

    const customer: Customer = {
      id,
      type: data.type,
      companyName: data.companyName,
      firstName: data.firstName,
      lastName: data.lastName,
      vatNumber: data.vatNumber,
      fiscalCode: data.fiscalCode,
      email: data.email,
      phone: data.phone,
      address: data.address,
      sdiRecipientCode: data.sdiRecipientCode,
      pec: data.pec,
      paymentTerms: data.paymentTerms || 'net30',
      creditLimit: data.creditLimit,
      notes: data.notes,
      isActive: true,
      createdAt: now,
      updatedAt: now,
    };

    customersStore.set(id, customer);

    // Audit log
    await auditTrailService.log({
      userId: 'system',
      action: 'CREATE',
      entityType: 'TRANSACTION',
      entityId: id,
      newValue: customer,
      metadata: { type: 'customer' },
    });

    return createEnvelope(customer);
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to create customer');
  }
}

/**
 * Get customer by ID
 */
export async function getCustomer(id: string): Promise<Envelope<Customer>> {
  const customer = customersStore.get(id);

  if (!customer) {
    return createErrorEnvelope('NOT_FOUND', `Customer ${id} not found`);
  }

  return createEnvelope(customer);
}

/**
 * Update customer
 */
export async function updateCustomer(
  id: string,
  updates: CustomerUpdateData
): Promise<Envelope<Customer>> {
  const customer = customersStore.get(id);

  if (!customer) {
    return createErrorEnvelope('NOT_FOUND', `Customer ${id} not found`);
  }

  // Validate VAT number if being updated
  if (updates.vatNumber && !isValidItalianVAT(updates.vatNumber)) {
    return createErrorEnvelope('VALIDATION_ERROR', 'Invalid Italian VAT number (Partita IVA)');
  }

  // Validate fiscal code if being updated
  if (updates.fiscalCode && !isValidItalianFiscalCode(updates.fiscalCode)) {
    return createErrorEnvelope('VALIDATION_ERROR', 'Invalid Italian fiscal code (Codice Fiscale)');
  }

  // Validate SDI code if being updated
  if (updates.sdiRecipientCode && !isValidSDICode(updates.sdiRecipientCode)) {
    return createErrorEnvelope('VALIDATION_ERROR', 'Invalid SDI recipient code');
  }

  const updatedCustomer: Customer = {
    ...customer,
    ...updates,
    // Merge address if provided
    address: updates.address ? { ...customer.address, ...updates.address } : customer.address,
    updatedAt: new Date().toISOString(),
  };

  customersStore.set(id, updatedCustomer);

  // Audit log
  await auditTrailService.log({
    userId: 'system',
    action: 'UPDATE',
    entityType: 'TRANSACTION',
    entityId: id,
    previousValue: customer,
    newValue: updatedCustomer,
    metadata: { type: 'customer' },
  });

  return createEnvelope(updatedCustomer);
}

/**
 * Delete customer
 */
export async function deleteCustomer(id: string): Promise<Envelope<void>> {
  const customer = customersStore.get(id);

  if (!customer) {
    return createErrorEnvelope('NOT_FOUND', `Customer ${id} not found`);
  }

  // TODO: Check if customer has invoices - if so, cannot delete (only deactivate)

  const deleted = customersStore.delete(id);

  if (!deleted) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to delete customer');
  }

  // Audit log
  await auditTrailService.log({
    userId: 'system',
    action: 'DELETE',
    entityType: 'TRANSACTION',
    entityId: id,
    previousValue: customer,
    metadata: { type: 'customer' },
  });

  return createEnvelope(undefined);
}

/**
 * List customers with optional filtering
 */
export async function listCustomers(
  filters?: CustomerFilters
): Promise<Envelope<Customer[]>> {
  try {
    let customers = Array.from(customersStore.values());

    if (filters) {
      // Filter by type
      if (filters.type) {
        customers = customers.filter((c) => c.type === filters.type);
      }

      // Filter by active status
      if (filters.isActive !== undefined) {
        customers = customers.filter((c) => c.isActive === filters.isActive);
      }

      // Filter by has e-invoice setup
      if (filters.hasEInvoiceSetup) {
        customers = customers.filter((c) => c.sdiRecipientCode || c.pec);
      }

      // Search in name, email, VAT
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        customers = customers.filter((c) => {
          const displayName = getCustomerDisplayName(c).toLowerCase();
          return (
            displayName.includes(searchLower) ||
            c.email.toLowerCase().includes(searchLower) ||
            (c.vatNumber && c.vatNumber.toLowerCase().includes(searchLower)) ||
            (c.fiscalCode && c.fiscalCode.toLowerCase().includes(searchLower))
          );
        });
      }
    }

    // Sort by display name
    customers.sort((a, b) => {
      const nameA = getCustomerDisplayName(a).toLowerCase();
      const nameB = getCustomerDisplayName(b).toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return createEnvelope(customers);
  } catch (error) {
    return createErrorEnvelope('INTERNAL_ERROR', 'Failed to list customers');
  }
}

/**
 * Get customer with invoice statistics
 */
export async function getCustomerWithStats(
  id: string
): Promise<Envelope<CustomerWithStats>> {
  const customer = customersStore.get(id);

  if (!customer) {
    return createErrorEnvelope('NOT_FOUND', `Customer ${id} not found`);
  }

  // TODO: Calculate actual stats from invoices
  const customerWithStats: CustomerWithStats = {
    ...customer,
    totalInvoices: 0,
    totalRevenue: 0,
    outstandingAmount: 0,
    lastInvoiceDate: undefined,
  };

  return createEnvelope(customerWithStats);
}

/**
 * Deactivate customer (soft delete)
 */
export async function deactivateCustomer(id: string): Promise<Envelope<void>> {
  const customer = customersStore.get(id);

  if (!customer) {
    return createErrorEnvelope('NOT_FOUND', `Customer ${id} not found`);
  }

  const updatedCustomer: Customer = {
    ...customer,
    isActive: false,
    updatedAt: new Date().toISOString(),
  };

  customersStore.set(id, updatedCustomer);
  return createEnvelope(undefined);
}

/**
 * Reactivate customer
 */
export async function reactivateCustomer(id: string): Promise<Envelope<void>> {
  const customer = customersStore.get(id);

  if (!customer) {
    return createErrorEnvelope('NOT_FOUND', `Customer ${id} not found`);
  }

  const updatedCustomer: Customer = {
    ...customer,
    isActive: true,
    updatedAt: new Date().toISOString(),
  };

  customersStore.set(id, updatedCustomer);
  return createEnvelope(undefined);
}

/**
 * Get customer by VAT number
 */
export async function getCustomerByVat(
  vatNumber: string
): Promise<Envelope<Customer | null>> {
  const vat = vatNumber.replace(/^IT/i, '').trim();

  const customers = Array.from(customersStore.values());
  const customer = customers.find((c) => {
    if (!c.vatNumber) return false;
    const customerVat = c.vatNumber.replace(/^IT/i, '').trim();
    return customerVat === vat;
  });

  return createEnvelope(customer || null);
}

/**
 * Get customer by fiscal code
 */
export async function getCustomerByFiscalCode(
  fiscalCode: string
): Promise<Envelope<Customer | null>> {
  const code = fiscalCode.toUpperCase().trim();

  const customers = Array.from(customersStore.values());
  const customer = customers.find((c) => {
    if (!c.fiscalCode) return false;
    return c.fiscalCode.toUpperCase().trim() === code;
  });

  return createEnvelope(customer || null);
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Clear all customers (for testing)
 */
export function clearCustomers(): void {
  customersStore.clear();
  customerCounter = 1;
}

/**
 * Seed sample customers
 * @deprecated Use real data from database. This function is disabled.
 */
export async function seedSampleCustomers(): Promise<void> {
  console.warn('[Customers API] seedSampleCustomers is disabled. Use real data from database.');
}
